import { isDateInRange as isDateInRangeUtil } from '@va/util/helpers';
import dayjs, { Dayjs } from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';
import { DateInputView, PickerConfig, PickerViewState } from './types';

dayjs.extend(weekday);
dayjs.extend(localeData);

type DateInputContextType = {
  pickerViewState: PickerViewState;
  setYear: (year: number) => void;
  setMonth: (month: number) => void;
  setView: (view: DateInputView) => void;
  pickedDate: Dayjs | undefined;
  setPickedDate: (date: Dayjs) => void;
  pickerConfig: PickerConfig & { yearsCount: number };
  yearViewLowestYear: number;
  isDateInRange: (date: Dayjs, interval?: dayjs.OpUnitType) => boolean;
  showNextYears: () => void;
  showPrevYears: () => void;
  showNextMonth: () => void;
  showPrevMonth: () => void;
};

type DateInputContextProps = {
  onChange?: (value: Dayjs) => void;
  value: Dayjs | undefined;
  pickerConfig?: PickerConfig;
};

const defaultContextValue: DateInputContextType = {
  pickerViewState: {
    year: dayjs().year(),
    month: dayjs().month(),
    view: DateInputView.Day,
    canShowNext: false,
    canShowPrev: false,
  },
  setYear: () => {},
  setMonth: () => {},
  setView: () => {},
  pickedDate: undefined,
  setPickedDate: () => {},
  pickerConfig: {
    yearsCount: 12,
  },
  yearViewLowestYear: dayjs().year() - 6,
  isDateInRange: () => false,
  showNextYears: () => {},
  showPrevYears: () => {},
  showNextMonth: () => {},
  showPrevMonth: () => {},
};

const DateInputContext = createContext<DateInputContextType>(defaultContextValue);

export const DateInputContextProvider = ({
  children,
  pickerConfig = defaultContextValue.pickerConfig,
  onChange,
  value,
}: PropsWithChildren<DateInputContextProps>) => {
  const yearsCount = pickerConfig?.yearsCount ?? 12;

  const [pickerViewState, setPickerViewState] = useState<PickerViewState>(defaultContextValue.pickerViewState);
  const [yearViewLowestYear, setYearViewLowestYear] = useState<number>(dayjs().year() - yearsCount / 2);

  const setYear = useCallback((year: number) => {
    setPickerViewState((prevState) => ({
      ...prevState,
      year,
    }));
  }, []);

  const setMonth = useCallback((month: number) => {
    setPickerViewState((prevState) => ({
      ...prevState,
      month,
    }));
  }, []);

  const setView = useCallback(
    (view: DateInputView) => {
      setPickerViewState((prevState) => {
        if (prevState.view !== DateInputView.Year && view === DateInputView.Year) {
          setYearViewLowestYear(pickerViewState.year - yearsCount / 2);
        }

        return {
          ...prevState,
          view,
        };
      });
    },
    [pickerViewState.year, yearsCount],
  );

  const handleDateChange = useCallback(
    (date: Dayjs) => {
      onChange?.(date);

      if (date.month() !== pickerViewState.month) {
        setMonth(date.month());
        setYear(date.year());
      }
    },
    [onChange, pickerViewState.month, setMonth, setYear],
  );

  const showNextYears = useCallback(() => {
    setYearViewLowestYear((prevState) => prevState + yearsCount);
  }, [yearsCount]);

  const showPrevYears = useCallback(() => {
    setYearViewLowestYear((prevState) => prevState - yearsCount);
  }, [yearsCount]);

  const showNextMonth = useCallback(() => {
    setPickerViewState((prevState) => {
      if (prevState.month === 11) {
        return {
          ...prevState,
          month: 0,
          year: prevState.year + 1,
        };
      }

      return {
        ...prevState,
        month: prevState.month + 1,
      };
    });
  }, []);

  const showPrevMonth = useCallback(() => {
    setPickerViewState((prevState) => {
      if (prevState.month === 0) {
        return {
          ...prevState,
          month: 11,
          year: prevState.year - 1,
        };
      }

      return {
        ...prevState,
        month: prevState.month - 1,
      };
    });
  }, []);

  const isDateInRange = useCallback(
    (date: Dayjs, interval?: dayjs.OpUnitType) => {
      const { minDate, maxDate } = pickerConfig;

      return isDateInRangeUtil(date, minDate, maxDate, interval);
    },
    [pickerConfig],
  );

  const canShowNext = useMemo(() => {
    if (pickerViewState.view === DateInputView.Day) {
      const date = dayjs().year(pickerViewState.year).month(pickerViewState.month).day(1);
      const lastDayOfMonth = date.endOf('month');

      return isDateInRange(lastDayOfMonth.add(1, 'day'), 'day');
    }

    if (pickerViewState.view === DateInputView.Year) {
      return isDateInRange(dayjs().year(yearViewLowestYear + yearsCount), 'year');
    }

    return true;
  }, [
    isDateInRange,
    pickerViewState.month,
    pickerViewState.view,
    pickerViewState.year,
    yearViewLowestYear,
    yearsCount,
  ]);

  const canShowPrev = useMemo(() => {
    if (pickerViewState.view === DateInputView.Day) {
      const date = dayjs().year(pickerViewState.year).month(pickerViewState.month).day(1);
      const firstDayOfMonth = date.startOf('month');

      return isDateInRange(firstDayOfMonth.subtract(1, 'day'), 'day');
    }

    if (pickerViewState.view === DateInputView.Year) {
      return isDateInRange(dayjs().year(yearViewLowestYear - 1), 'year');
    }

    return true;
  }, [isDateInRange, pickerViewState.month, pickerViewState.view, pickerViewState.year, yearViewLowestYear]);

  return (
    <DateInputContext.Provider
      value={{
        setYear,
        setView,
        setMonth,
        pickerViewState: { ...pickerViewState, canShowNext, canShowPrev },
        pickedDate: value,
        setPickedDate: handleDateChange,
        pickerConfig: { ...pickerConfig, yearsCount },
        yearViewLowestYear,
        showNextYears,
        showPrevYears,
        showNextMonth,
        showPrevMonth,
        isDateInRange,
      }}
    >
      {children}
    </DateInputContext.Provider>
  );
};

export const useDateInputContext = () => useContext(DateInputContext);
